/**************
 Announcement bar

 Two jobs. The info popover toggle, and - when the section's "Show only to shoppers on the
 untagged-traffic offer" box is ticked - keeping the offer copy and the "another discount"
 flag in step with the discount system (assets/theme-discount.js).

 Visibility itself is CSS: sections/theme-announcement.liquid shows the offer copy when
 <html data-dp-source="traffic">, the backup copy (if any) otherwise, and that attribute is
 stamped in <head> before first paint and kept current by publish() in theme-discount.js. Nothing
 here toggles display - the one thing CSS cannot know is what ELSE is on the cart, and that is
 the <html data-announcement-foreign> flag below, which moves the bar from the offer copy to the
 backup copy. The verdict is remembered per cart so the next pageview paints right first time.
 ***************/

document.addEventListener("DOMContentLoaded", () => {
    setupAnnouncementPopover();
    setupAnnouncementOffer();
});

/********************* Popover *********************/

function setupAnnouncementPopover() {
    // one button + popover per state of the bar (offer copy, backup copy); the section renders
    // the pair through snippets/announcement-info.liquid, button first, popover next
    const groups = Array.from(document.querySelectorAll(".announcementInfo"))
        .map((infoBtn) => ({infoBtn, popover: infoBtn.nextElementSibling}))
        .filter((g) => g.popover && g.popover.classList.contains("announcementPopover"));
    if (!groups.length) {
        return;
    }

    const close = (g) => {
        g.popover.classList.remove("active");
        g.infoBtn.setAttribute("aria-expanded", "false");
    };

    groups.forEach((g) => {
        // toggle the info popover when the icon is clicked
        g.infoBtn.addEventListener("click", () => {
            const willOpen = !g.popover.classList.contains("active");
            g.popover.classList.toggle("active", willOpen);
            g.infoBtn.setAttribute("aria-expanded", willOpen);
        });
    });

    // close the popovers when clicking outside the icon or popover
    document.addEventListener("mousedown", (e) => {
        if (e.target.closest(".announcementInfo") || e.target.closest(".announcementPopover")) {
            return;
        }
        groups.forEach(close);
    });
}

/********************* Offer-only mode *********************/

function setupAnnouncementOffer() {
    const bar = document.querySelector("#announcement[data-announcement-offer-only]");
    if (!bar) {
        return;
    }
    const templated = Array.from(bar.querySelectorAll("[data-template]"));
    const html = document.documentElement;

    const render = () => {
        /*
         "Another discount" = any code on the cart that is not the offer's own. Read off the
         discount module's state rather than the cookie: the cart is the truth (theme-discount.js
         header), and codesOnCart is exactly /cart.js discount_codes. A prefix match covers a rule
         whose code is matched by prefix (Klaviyo unique coupons, SNAP25-A1B2). Two codes of any
         kind is also "another discount", whichever they are.

         Only ever judged while OUR rule is the active one - if a foreign code is all the cart
         holds, publish(null) has already removed data-dp-source and CSS has hidden the bar.
        */
        const api = window.OhSnapDiscount;
        const state = api && !api.provisional ? api.state() : null;
        let foreign = false;
        if (state && state.code) {
            const own = String(state.code).toUpperCase();
            const codes = (state.codesOnCart || []).map((code) => String(code).toUpperCase());
            foreign = codes.length > 1 || codes.some((code) => code !== own && code.indexOf(own) !== 0);
        }
        /*
         The flag lives on <html>, not the bar, because the inline seed in the section (which runs
         before the bar exists) has to be able to set it too. Only a CONFIRMED cart read may write
         the memory: before /cart.js answers, codesOnCart is empty and would wrongly clear a verdict
         the seed just replayed. Keyed to the cart cookie, so a new cart starts clean.
        */
        if (!state || state.confirmed) {
            html.dataset.announcementForeign = foreign ? "1" : "0";
        }
        if (state && state.confirmed) {
            try {
                const m = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
                window.localStorage.setItem("ohsnapAnnouncement:foreign", JSON.stringify({cart: m ? m[1] : "", foreign: foreign ? 1 : 0}));
            } catch (e) {
                /* private mode: the flag is still set above for this pageview */
            }
        }

        /*
         {percent} and {threshold} resolve against data-dp-advertised and data-dp-minimum - the
         chokepoints the PDP chip and the mini-cart tracker read (theme-mini-cart.js), so all three
         surfaces quote one number, and one that follows the depth actually observed on a cart.
         Nothing stamped (design mode, or no offer on this session) leaves the Liquid render as is:
         it was filled from the metaobject and is right for the editor, and the bar is hidden for
         the shopper anyway.
        */
        const percent = parseFloat(html.dataset.dpAdvertised) || 0;
        const minimum = parseInt(html.dataset.dpMinimum, 10) || 0;
        if (percent <= 0) {
            return;
        }
        const percentText = String(Math.round(percent * 10) / 10);
        /*
         The minimum is stamped in the shopper's currency. Converted from the shop's, it is rounded
         up to the whole unit - "orders over $98", not "$97.62" - exactly as the Liquid first paint
         and the PDP chip do it (shownMinimum() in theme-discount.js), so no surface quotes a
         different figure.
        */
        const config = window.OhSnapDiscountConfig || {};
        const converted = !!config.currency && !!config.shopCurrency && config.currency !== config.shopCurrency;
        const shown = converted ? Math.ceil(minimum / 100) * 100 : minimum;
        const thresholdText = typeof price === "function" ? price(shown, true) : "$" + Math.round(shown / 100);
        templated.forEach((el) => {
            const next = el.dataset.template
                .replace(/\{percent\}/g, percentText)
                .replace(/\{threshold\}/g, thresholdText);
            if (el.innerHTML !== next) {
                el.innerHTML = next;
            }
        });
    };

    render();
    // discount:changed also fires when the set of codes on the cart changes (publish() in
    // theme-discount.js); cart:updated is the drawer's own refresh
    document.addEventListener("discount:changed", render);
    document.addEventListener("cart:updated", render);
}
